import { useEffect, useMemo, useState } from "react";
import {
  Plus, Pencil, Trash2, Search, Settings, Ruler, Wallet, Grid3x3, Save, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * Halaman Konfigurasi Kebijakan SPPD (Admin only).
 * - Tab 1: Pengaturan Umum (penandatangan ST, maks hari, penandatangan Section 8)
 * - Tab 2: Kualifikasi Jarak (CRUD list)
 * - Tab 3: Komponen Biaya (CRUD list)
 * - Tab 4: Matriks Tarif (matriks Kualifikasi × Jabatan × Komponen → nilai)
 */
export default function AdminSppdKonfigurasi() {
  const [tab, setTab] = useState("umum");

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-sppd-konfigurasi">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
          Konfigurasi Kebijakan
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
          Surat Perintah Perjalanan Dinas (SPPD)
        </h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
          Kelola penandatangan Surat Tugas &amp; SPPD, kualifikasi jarak, komponen biaya,
          dan matriks tarif per jabatan.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1" data-testid="sppd-tabs">
          <TabsTrigger value="umum" className="gap-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white" data-testid="sppd-tab-umum"><Settings size={14} />Pengaturan Umum</TabsTrigger>
          <TabsTrigger value="kualifikasi" className="gap-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white" data-testid="sppd-tab-kualifikasi"><Ruler size={14} />Kualifikasi Jarak</TabsTrigger>
          <TabsTrigger value="komponen" className="gap-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white" data-testid="sppd-tab-komponen"><Wallet size={14} />Komponen Biaya</TabsTrigger>
          <TabsTrigger value="tarif" className="gap-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white" data-testid="sppd-tab-tarif"><Grid3x3 size={14} />Matriks Tarif</TabsTrigger>
        </TabsList>
        <TabsContent value="umum" className="mt-6"><SettingsTab /></TabsContent>
        <TabsContent value="kualifikasi" className="mt-6">
          <SimpleCrudTab endpoint="/sppd/kualifikasi" label="Kualifikasi Jarak" placeholder="Contoh: >100 KM" testPrefix="sppd-kualifikasi" showMaxKm />
        </TabsContent>
        <TabsContent value="komponen" className="mt-6">
          <SimpleCrudTab endpoint="/sppd/komponen" label="Komponen Biaya" placeholder="Contoh: Uang Harian/Uang Makan" testPrefix="sppd-komponen" />
        </TabsContent>
        <TabsContent value="tarif" className="mt-6"><MatriksTarifTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ========================================================================
// TAB 1 — Pengaturan Umum
// ========================================================================

function SettingsTab() {
  const [form, setForm] = useState({
    penandatangan_st_user_id: "", penandatangan_st_nama: "", penandatangan_st_jabatan: "",
    max_hari_per_perintah: 14,
    kadiv_keu_user_id: "", kadiv_keu_nama: "",
    direktur_user_id: "", direktur_nama: "",
    direktur_utama_user_id: "", direktur_utama_nama: "",
  });
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/sppd/settings"),
          api.get("/sppd/penandatangan-candidates"),
        ]);
        setForm({
          penandatangan_st_user_id: s.data.penandatangan_st_user_id || "",
          penandatangan_st_nama: s.data.penandatangan_st_nama || "",
          penandatangan_st_jabatan: s.data.penandatangan_st_jabatan || "",
          max_hari_per_perintah: s.data.max_hari_per_perintah || 14,
          kadiv_keu_user_id: s.data.kadiv_keu_user_id || "",
          kadiv_keu_nama: s.data.kadiv_keu_nama || "",
          direktur_user_id: s.data.direktur_user_id || "",
          direktur_nama: s.data.direktur_nama || "",
          direktur_utama_user_id: s.data.direktur_utama_user_id || "",
          direktur_utama_nama: s.data.direktur_utama_nama || "",
        });
        setCandidates(c.data || []);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function pickUser(roleKey, uid) {
    const u = candidates.find((x) => x.id === uid);
    const extra = roleKey === "penandatangan_st"
      ? { penandatangan_st_user_id: uid, penandatangan_st_nama: u?.name || "", penandatangan_st_jabatan: u?.jabatan || form.penandatangan_st_jabatan }
      : roleKey === "kadiv_keu"
      ? { kadiv_keu_user_id: uid, kadiv_keu_nama: u?.name || "" }
      : roleKey === "direktur"
      ? { direktur_user_id: uid, direktur_nama: u?.name || "" }
      : { direktur_utama_user_id: uid, direktur_utama_nama: u?.name || "" };
    setForm({ ...form, ...extra });
  }

  async function save() {
    if (!form.penandatangan_st_nama || !form.penandatangan_st_jabatan) {
      toast.error("Nama & Jabatan penandatangan Surat Tugas wajib diisi");
      return;
    }
    if (!form.max_hari_per_perintah || form.max_hari_per_perintah < 1) {
      toast.error("Maksimal hari harus ≥ 1");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        penandatangan_st_user_id: form.penandatangan_st_user_id || null,
        penandatangan_st_nama: form.penandatangan_st_nama.trim(),
        penandatangan_st_jabatan: form.penandatangan_st_jabatan.trim(),
        max_hari_per_perintah: Number(form.max_hari_per_perintah),
        kadiv_keu_user_id: form.kadiv_keu_user_id || null,
        kadiv_keu_nama: form.kadiv_keu_nama.trim(),
        direktur_user_id: form.direktur_user_id || null,
        direktur_nama: form.direktur_nama.trim(),
        direktur_utama_user_id: form.direktur_utama_user_id || null,
        direktur_utama_nama: form.direktur_utama_nama.trim(),
      };
      await api.put("/sppd/settings", payload);
      toast.success("Pengaturan SPPD tersimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-slate-400 text-center py-10">Memuat pengaturan…</div>;

  return (
    <div className="space-y-6" data-testid="sppd-settings-form">
      {/* Penandatangan Surat Tugas */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <UserCheck size={16} className="text-teal-700" />
          <div className="text-sm font-bold uppercase tracking-wider text-slate-700">Penandatangan Surat Tugas</div>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Pejabat yang akan menerima notifikasi untuk menyetujui &amp; menerbitkan Surat Tugas.
          Namanya akan muncul di bawah QR code tanda tangan pada dokumen PDF.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Pilih User (opsional, auto-isi nama)</Label>
            <Select value={form.penandatangan_st_user_id || "manual"} onValueChange={(v) => pickUser("penandatangan_st", v === "manual" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="sppd-settings-pst-user"><SelectValue placeholder="— Isi manual —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">— Isi manual —</SelectItem>
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} ({c.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama *</Label>
            <Input className="mt-1.5" value={form.penandatangan_st_nama} onChange={(e) => setForm({ ...form, penandatangan_st_nama: e.target.value })} data-testid="sppd-settings-pst-nama" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Jabatan *</Label>
            <Input className="mt-1.5" placeholder="Contoh: Plt. Direktur Utama" value={form.penandatangan_st_jabatan} onChange={(e) => setForm({ ...form, penandatangan_st_jabatan: e.target.value })} data-testid="sppd-settings-pst-jabatan" />
          </div>
        </div>
      </div>

      {/* Maks hari */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">Batasan Perjalanan Dinas</div>
        <div className="max-w-xs">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Maksimal Jumlah Hari per 1x Perintah</Label>
          <Input type="number" min={1} max={90} className="mt-1.5" value={form.max_hari_per_perintah} onChange={(e) => setForm({ ...form, max_hari_per_perintah: e.target.value })} data-testid="sppd-settings-max-hari" />
          <p className="text-[11px] text-slate-500 mt-1">Validasi saat pengaju membuat perintah (akhir − awal + 1).</p>
        </div>
      </div>

      {/* Penandatangan Section 8 */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <UserCheck size={16} className="text-teal-700" />
          <div className="text-sm font-bold uppercase tracking-wider text-slate-700">Penandatangan Form SPPD (Section 8)</div>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Nama 3 pejabat yang muncul pada Section 8 Form SPPD sebagai kolom tanda tangan.
          Tanda tangan digital berupa QR code akan di-render di sampingnya pada PDF.
        </p>
        <div className="space-y-4">
          {[
            { k: "kadiv_keu", label: "KADIV KEU & ADMIN" },
            { k: "direktur", label: "DIREKTUR" },
            { k: "direktur_utama", label: "DIREKTUR UTAMA" },
          ].map((r) => (
            <div key={r.k} className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="md:col-span-2 text-[11px] font-bold uppercase tracking-wider text-teal-700">{r.label}</div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Pilih User (opsional)</Label>
                <Select
                  value={form[`${r.k}_user_id`] || "manual"}
                  onValueChange={(v) => pickUser(r.k, v === "manual" ? "" : v)}
                >
                  <SelectTrigger className="mt-1.5" data-testid={`sppd-settings-${r.k}-user`}><SelectValue placeholder="— Isi manual —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">— Isi manual —</SelectItem>
                    {candidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name} ({c.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama</Label>
                <Input
                  className="mt-1.5"
                  value={form[`${r.k}_nama`]}
                  onChange={(e) => setForm({ ...form, [`${r.k}_nama`]: e.target.value })}
                  data-testid={`sppd-settings-${r.k}-nama`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="gap-2 bg-teal-700 hover:bg-teal-800"
          data-testid="sppd-settings-save-btn"
        >
          <Save size={16} /> {saving ? "Menyimpan…" : "Simpan Pengaturan"}
        </Button>
      </div>
    </div>
  );
}

// ========================================================================
// TAB 2/3 — Kualifikasi & Komponen (CRUD generik)
// ========================================================================

function SimpleCrudTab({ endpoint, label, placeholder, testPrefix, showMaxKm = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nama: "", urutan: 100, keterangan: "", is_active: true, max_km: "" });

  async function load() {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setRows(data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.nama || "").toLowerCase().includes(q));
  }, [rows, search]);

  function openCreate() {
    setEditing(null);
    setForm({ nama: "", urutan: 100, keterangan: "", is_active: true, max_km: "" });
    setOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      nama: row.nama || "", urutan: row.urutan ?? 100,
      keterangan: row.keterangan || "", is_active: row.is_active !== false,
      max_km: row.max_km == null ? "" : String(row.max_km),
    });
    setOpen(true);
  }
  async function save() {
    if (!form.nama.trim()) { toast.error("Nama wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = {
        nama: form.nama.trim(),
        urutan: Number(form.urutan) || 100,
        keterangan: form.keterangan.trim() || null,
        is_active: !!form.is_active,
      };
      if (showMaxKm) {
        const mk = String(form.max_km).trim();
        payload.max_km = mk === "" ? null : Number(mk);
      }
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success(`${label} diperbarui`);
      } else {
        await api.post(endpoint, payload);
        toast.success(`${label} ditambahkan`);
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  }
  async function del(row) {
    if (!confirm(`Hapus ${label} "${row.nama}"?\n\nSemua baris matriks tarif terkait juga akan dihapus.`)) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success(`${row.nama} dihapus`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }

  return (
    <div data-testid={`${testPrefix}-section`}>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative sm:max-w-xs flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder={`Cari ${label.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid={`${testPrefix}-search`} />
        </div>
        <Button onClick={openCreate} className="gap-2 bg-teal-700 hover:bg-teal-800" data-testid={`${testPrefix}-add-btn`}>
          <Plus size={16} /> Tambah {label}
        </Button>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-20 uppercase text-[10px] tracking-widest font-bold text-slate-500">Urutan</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama</TableHead>
              {showMaxKm && <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-28 text-right">Maks. KM</TableHead>}
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Keterangan</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-32 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={showMaxKm ? 5 : 4} className="text-center text-slate-400 py-10">Memuat…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={showMaxKm ? 5 : 4} className="text-center text-slate-400 py-10">
                Belum ada {label.toLowerCase()}. Klik tombol di atas untuk menambahkan.
              </TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.id} data-testid={`${testPrefix}-row-${r.id}`}>
                <TableCell><span className="font-mono-clock text-xs text-slate-600">{r.urutan ?? "—"}</span></TableCell>
                <TableCell><span className="font-semibold text-slate-900">{r.nama}</span></TableCell>
                {showMaxKm && <TableCell className="text-right"><span className="font-mono-clock text-xs text-slate-700">{r.max_km == null ? <span className="text-slate-300">∞</span> : `${r.max_km} km`}</span></TableCell>}
                <TableCell><span className="text-slate-600 text-sm">{r.keterangan || <span className="text-slate-300">—</span>}</span></TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`${testPrefix}-edit-${r.id}`}>
                      <Pencil size={14} className="text-teal-700" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`${testPrefix}-del-${r.id}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${label}` : `Tambah ${label}`}</DialogTitle>
            <DialogDescription>Isi nama &amp; urutan tampil pada dropdown.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama</Label>
              <Input className="mt-1.5" placeholder={placeholder} value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} maxLength={80} data-testid={`${testPrefix}-input-nama`} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Urutan</Label>
              <Input type="number" className="mt-1.5" value={form.urutan} onChange={(e) => setForm({ ...form, urutan: e.target.value })} data-testid={`${testPrefix}-input-urutan`} />
            </div>
            {showMaxKm && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Maks. Jarak (km)</Label>
                <Input type="number" step="0.1" className="mt-1.5" placeholder="Kosongkan untuk 'tak terbatas' (fallback)" value={form.max_km} onChange={(e) => setForm({ ...form, max_km: e.target.value })} data-testid={`${testPrefix}-input-maxkm`} />
                <div className="text-[11px] text-slate-500 mt-1">Dipakai untuk auto-pilih kualifikasi saat atasan menghitung jarak Google Maps.</div>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Keterangan (opsional)</Label>
              <Textarea className="mt-1.5" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} maxLength={200} rows={2} data-testid={`${testPrefix}-input-keterangan`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testPrefix}-cancel-btn`}>Batal</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid={`${testPrefix}-save-btn`}>
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========================================================================
// TAB 4 — Matriks Tarif
// ========================================================================

function MatriksTarifTab() {
  const [kualifikasi, setKualifikasi] = useState([]);
  const [komponen, setKomponen] = useState([]);
  const [jabatan, setJabatan] = useState([]);
  const [selKual, setSelKual] = useState("");
  const [selJab, setSelJab] = useState("");
  const [rows, setRows] = useState({}); // { komponen_id: nilai }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [k, c, j] = await Promise.all([
          api.get("/sppd/kualifikasi"),
          api.get("/sppd/komponen"),
          api.get("/master/jabatan"),
        ]);
        setKualifikasi(k.data || []);
        setKomponen((c.data || []).filter((x) => x.is_active !== false));
        setJabatan(j.data || []);
      } catch (e) { toast.error(apiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!selKual || !selJab) { setRows({}); return; }
    (async () => {
      try {
        const { data } = await api.get(`/sppd/tarif?kualifikasi_id=${selKual}&jabatan_id=${selJab}`);
        const map = {};
        (data || []).forEach((r) => { map[r.komponen_id] = r.nilai; });
        setRows(map);
      } catch (e) { toast.error(apiErrorMessage(e)); }
    })();
  }, [selKual, selJab]);

  async function save() {
    if (!selKual || !selJab) { toast.error("Pilih kualifikasi & jabatan"); return; }
    setSaving(true);
    try {
      const payload = {
        kualifikasi_id: selKual,
        jabatan_id: selJab,
        rows: komponen.map((k) => ({ komponen_id: k.id, nilai: Number(rows[k.id] || 0) })),
      };
      const { data } = await api.put("/sppd/tarif/bulk", payload);
      toast.success(`Tarif tersimpan — ${data.saved} baris disimpan, ${data.deleted} dihapus`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="text-slate-400 text-center py-10">Memuat matriks…</div>;

  const noMaster = kualifikasi.length === 0 || komponen.length === 0 || jabatan.length === 0;

  return (
    <div className="space-y-4" data-testid="sppd-matriks-tarif">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">Matriks Tarif per Kombinasi</div>
        <p className="text-xs text-slate-500">
          Pilih <b>Kualifikasi Jarak</b> + <b>Jabatan</b> → isi nominal tarif untuk tiap komponen biaya.
          Baris dengan nilai 0 akan dihapus dari matriks.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kualifikasi Jarak</Label>
            <Select value={selKual} onValueChange={setSelKual}>
              <SelectTrigger className="mt-1.5" data-testid="sppd-tarif-kualifikasi"><SelectValue placeholder="Pilih kualifikasi…" /></SelectTrigger>
              <SelectContent>
                {kualifikasi.map((k) => (
                  <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Jabatan</Label>
            <Select value={selJab} onValueChange={setSelJab}>
              <SelectTrigger className="mt-1.5" data-testid="sppd-tarif-jabatan"><SelectValue placeholder="Pilih jabatan…" /></SelectTrigger>
              <SelectContent>
                {jabatan.map((j) => (
                  <SelectItem key={j.id} value={j.id}>{j.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {noMaster && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm p-3">
          Lengkapi dulu Kualifikasi Jarak, Komponen Biaya, dan master Jabatan sebelum mengisi matriks tarif.
        </div>
      )}

      {selKual && selJab && !noMaster && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Komponen Biaya</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-56 text-right">Nilai (Rp)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {komponen.map((k) => (
                <TableRow key={k.id} data-testid={`sppd-tarif-row-${k.id}`}>
                  <TableCell><span className="font-semibold text-slate-900">{k.nama}</span></TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min={0}
                      className="text-right"
                      value={rows[k.id] ?? 0}
                      onChange={(e) => setRows({ ...rows, [k.id]: e.target.value })}
                      data-testid={`sppd-tarif-input-${k.id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end p-4 border-t border-slate-200 bg-slate-50">
            <Button onClick={save} disabled={saving} className="gap-2 bg-teal-700 hover:bg-teal-800" data-testid="sppd-tarif-save-btn">
              <Save size={16} /> {saving ? "Menyimpan…" : "Simpan Matriks"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
